/**
 * ProjectUpdatesFeed — Feb 2026 · Phase 84.28
 *
 * Adds:
 *   • Image lightbox — clicking an update's image opens a full-screen viewer
 *   • Emoji reactions — 6 quick-hits (👍 ❤️ 😄 🎉 👀 👎) + a "+" popover
 *     that lets you pick from an extended set. Toggles per user.
 *   • Nested replies — one level of threading (Slack-style). Reply composer
 *     inlines under each parent; the reply row is visually indented + smaller.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Image as ImageIcon, Trash2, Send, Newspaper, ChevronDown, ChevronRight, Smile, MessageSquare, X } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/dateFormat";
import linkify from "@/lib/linkify";

const RECENT_LIMIT = 3;
const QUICK_EMOJIS = ["\u{1F44D}", "\u2764", "\u{1F604}", "\u{1F389}", "\u{1F440}", "\u{1F44E}"];
const EXTRA_EMOJIS = ["\u{1F525}", "\u{1F64F}", "\u{1F680}", "\u{1F4AF}", "\u2705", "\u274C", "\u{1F914}", "\u{1F602}", "\u{1F4A1}", "\u{1F64C}", "\u{1F9E0}", "\u2615", "\u{1F4CC}", "\u{1F3AF}", "\u26A1", "\u{1F3A8}", "\u{1F4C8}", "\u{1F41B}", "\u{1F973}", "\u{1F605}"];

export default function ProjectUpdatesFeed({ projectId }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [body, setBody] = useState("");
  const [media, setMedia] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [olderOpen, setOlderOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);  // {url, type}
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/project-updates", { params: { project_id: projectId } })
    .then((r) => setRows(r.data)).catch(() => {}), [projectId]);
  useEffect(() => { load(); }, [load]);

  // Split root updates from replies. Sort replies by created_at ascending under each root.
  const { roots, repliesByParent } = useMemo(() => {
    const rr = {};
    const rootList = [];
    for (const r of rows) {
      if (r.parent_id) (rr[r.parent_id] = rr[r.parent_id] || []).push(r);
      else rootList.push(r);
    }
    Object.values(rr).forEach((arr) => arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
    return { roots: rootList, repliesByParent: rr };
  }, [rows]);

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) { toast.error("File too large (50 MB max)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const mime = f.type || "";
      let type = "image";
      if (mime.startsWith("video/")) type = "video";
      else if (mime === "image/gif") type = "gif";
      setMedia({ url: data.path, type, name: data.filename || f.name });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const post = async (parentId = null, replyBody = null, replyMedia = null) => {
    const useBody = parentId ? replyBody : body;
    const useMedia = parentId ? replyMedia : media;
    if (!useBody?.trim() && !useMedia) { toast.error("Add a message or media"); return; }
    setSaving(true);
    try {
      await api.post("/project-updates", {
        project_id: projectId,
        body: useBody,
        media_url: useMedia?.url || null,
        media_type: useMedia?.type || null,
        parent_id: parentId,
      });
      if (!parentId) { setBody(""); setMedia(null); }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this update? Any replies will be removed too.")) return;
    try { await api.delete(`/project-updates/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const react = async (id, emoji) => {
    // Optimistic — flip the reaction locally before the round-trip.
    setRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      const next = { ...(r.reactions || {}) };
      const users = new Set(next[emoji] || []);
      users.has(user.id) ? users.delete(user.id) : users.add(user.id);
      if (users.size) next[emoji] = Array.from(users);
      else delete next[emoji];
      return { ...r, reactions: next };
    }));
    try { await api.post(`/project-updates/${id}/react`, { emoji }); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  };

  return (
    <div className="mt-8" data-testid="project-updates-feed">
      <div className="flex items-center gap-2 mb-3">
        <Newspaper size={18} className="text-[#F54800]" />
        <h2 className="font-display text-xl font-bold text-slate-900">Project updates</h2>
      </div>

      <div className="card-flat p-4">
        <Textarea rows={2} className="rounded-sm" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share a quick update with the team and client…" data-testid="update-body" />
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept="image/*,video/*,.gif" onChange={pickFile} className="hidden" data-testid="update-file-input" />
          <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="update-attach-btn">
            <ImageIcon size={12} className="mr-1.5" /> {uploading ? "Uploading…" : "Attach image / GIF / video"}
          </Button>
          {media && (
            <div className="text-xs text-slate-600 inline-flex items-center gap-2 bg-slate-50 rounded-sm px-2 py-1 border border-slate-100">
              {media.name} · {media.type}
              <button onClick={() => setMedia(null)} className="text-slate-400 hover:text-red-600"><Trash2 size={11} /></button>
            </div>
          )}
          <div className="ml-auto">
            <Button onClick={() => post()} disabled={saving} size="sm" className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="update-post-btn">
              <Send size={12} className="mr-1.5" /> Post update
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {roots.length === 0 && <div className="text-sm text-slate-500 italic px-1">No updates yet. Be the first to post.</div>}
        {roots.slice(0, RECENT_LIMIT).map((u) => (
          <UpdateRow key={u.id} u={u} user={user} replies={repliesByParent[u.id] || []} onRemove={remove} onReact={react} onReply={post} onOpenLightbox={setLightbox} projectId={projectId} />
        ))}
        {roots.length > RECENT_LIMIT && (
          <div className="border-t border-slate-100 pt-3" data-testid="project-updates-older-accordion">
            <button type="button" onClick={() => setOlderOpen((v) => !v)} className="w-full flex items-center justify-between text-xs text-slate-600 hover:text-slate-900 group" data-testid="project-updates-older-toggle" aria-expanded={olderOpen}>
              <span className="inline-flex items-center gap-1.5 font-medium">
                {olderOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                Older updates
                <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-sm">{roots.length - RECENT_LIMIT}</span>
              </span>
              <span className="text-[10px] text-slate-400 group-hover:text-slate-600">{olderOpen ? "Hide" : "Show"}</span>
            </button>
            {olderOpen && (
              <div className="space-y-3 mt-3">
                {roots.slice(RECENT_LIMIT).map((u) => (
                  <UpdateRow key={u.id} u={u} user={user} replies={repliesByParent[u.id] || []} onRemove={remove} onReact={react} onReply={post} onOpenLightbox={setLightbox} projectId={projectId} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lightbox modal — click image to open, click backdrop or X to close, Esc key too. */}
      {lightbox && <Lightbox media={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function UpdateRow({ u, user, replies, onRemove, onReact, onReply, onOpenLightbox, isReply = false }) {
  const mine = u.user_id === user?.id;
  const can = usePermissions();
  const isAdmin = can("projects_edit");
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const reactions = u.reactions || {};

  return (
    <div className={`card-flat p-4 ${isReply ? "ml-8 border-l-2 border-l-slate-200 rounded-l-none" : ""}`} data-testid={`update-${u.id}`}>
      <div className="flex items-start gap-3">
        {u.user_avatar ? (
          <img src={u.user_avatar} alt="" className={`${isReply ? "w-6 h-6" : "w-8 h-8"} rounded-full object-cover shrink-0`} />
        ) : (
          <div className={`${isReply ? "w-6 h-6 text-[10px]" : "w-8 h-8 text-xs"} rounded-full bg-[#F54800]/10 text-[#F54800] flex items-center justify-center font-bold shrink-0`}>{u.user_name?.[0] || "?"}</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="text-slate-900 font-medium">{u.user_name}</span>
            <span>· {u.user_role}</span>
            <span>· {formatDateTime(u.created_at)}</span>
          </div>
          {u.body && <p className={`${isReply ? "text-xs" : "text-sm"} text-slate-800 mt-1 whitespace-pre-wrap break-words`}>{linkify(u.body)}</p>}
          {u.media_url && (
            <button
              type="button"
              onClick={() => u.media_type !== "video" && onOpenLightbox({ url: u.media_url, type: u.media_type })}
              className={`mt-2 rounded-sm overflow-hidden border border-slate-100 bg-slate-50 max-w-md block ${u.media_type !== "video" ? "cursor-zoom-in hover:border-slate-300" : ""}`}
              data-testid={`update-media-${u.id}`}
            >
              {u.media_type === "video"
                ? <video src={u.media_url} controls className="w-full max-h-96" />
                : <img src={u.media_url} alt="Attachment — click to enlarge" className="w-full max-h-96 object-contain" />}
            </button>
          )}

          {/* Reactions row */}
          {Object.keys(reactions).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1" data-testid={`reactions-${u.id}`}>
              {Object.entries(reactions).map(([emoji, users]) => {
                const mineHere = users.includes(user?.id);
                return (
                  <button
                    key={emoji}
                    onClick={() => onReact(u.id, emoji)}
                    className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-sm border transition-colors ${mineHere ? "bg-[#F54800]/10 border-[#F54800]/40 text-[#F54800]" : "bg-slate-50 border-slate-200 hover:border-slate-400"}`}
                    data-testid={`reaction-${u.id}-${emoji}`}
                    title={mineHere ? "Click to un-react" : "Click to react"}
                  >
                    <span>{emoji}</span><span className="font-mono">{users.length}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Action row — quick react + reply button (root only). */}
          <div className="mt-2 flex items-center gap-1 flex-wrap">
            <div className="relative">
              <button
                onClick={() => setEmojiOpen((v) => !v)}
                className="text-[10px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm hover:bg-slate-100"
                data-testid={`react-open-${u.id}`}
                title="Add a reaction"
              >
                <Smile size={11} /> React
              </button>
              {emojiOpen && (
                <div className="absolute z-30 mt-1 left-0 bg-white border border-slate-200 rounded-sm shadow-lg p-2 flex flex-wrap gap-1 max-w-[220px]" data-testid={`emoji-panel-${u.id}`} onMouseLeave={() => setEmojiOpen(false)}>
                  {[...QUICK_EMOJIS, ...EXTRA_EMOJIS].map((e) => (
                    <button key={e} onClick={() => { onReact(u.id, e); setEmojiOpen(false); }} className="text-lg hover:bg-slate-100 rounded-sm w-7 h-7 leading-7" data-testid={`emoji-btn-${u.id}-${e}`}>{e}</button>
                  ))}
                </div>
              )}
            </div>
            {!isReply && (
              <button
                onClick={() => setShowReplyBox((v) => !v)}
                className="text-[10px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm hover:bg-slate-100"
                data-testid={`reply-open-${u.id}`}
              >
                <MessageSquare size={11} /> Reply{replies.length > 0 && ` (${replies.length})`}
              </button>
            )}
          </div>

          {/* Nested replies (1 level deep) — rendered via ReplyRow to avoid
              Babel's static self-reference traversal blowing the stack. */}
          {replies.length > 0 && (
            <div className="mt-3 space-y-2" data-testid={`replies-${u.id}`}>
              {replies.map((r) => (
                <ReplyRow key={r.id} u={r} user={user} onRemove={onRemove} onReact={onReact} onOpenLightbox={onOpenLightbox} />
              ))}
            </div>
          )}

          {/* Inline reply composer */}
          {showReplyBox && !isReply && (
            <div className="mt-2 flex items-start gap-2" data-testid={`reply-box-${u.id}`}>
              <Textarea rows={2} className="rounded-sm text-xs flex-1" placeholder={`Reply to ${u.user_name}…`} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} data-testid={`reply-body-${u.id}`} />
              <Button
                size="sm"
                className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white"
                disabled={!replyBody.trim()}
                onClick={() => { onReply(u.id, replyBody, null); setReplyBody(""); setShowReplyBox(false); }}
                data-testid={`reply-send-${u.id}`}
              >
                <Send size={11} />
              </Button>
            </div>
          )}
        </div>
        {(mine || isAdmin) && (
          <button onClick={() => onRemove(u.id)} className="text-slate-400 hover:text-red-600 shrink-0" data-testid={`del-update-${u.id}`}><Trash2 size={13} /></button>
        )}
      </div>
    </div>
  );
}

function Lightbox({ media, onClose }) {
  // Close on Esc — nothing else about the app should be able to catch this
  // while the lightbox is up.
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    // Lock body scroll while lightbox is up so the viewport stays put.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", h);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  // Phase 84.32 — render into document.body via portal so the fixed backdrop
  // escapes any ancestor that has `transform`/`filter` set (which would turn
  // `position: fixed` into "fixed to the ancestor" instead of the viewport).
  // Without this the image floats below-the-fold on some pages.
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="update-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-sm bg-white/10 hover:bg-white/20 z-10"
        data-testid="update-lightbox-close"
        aria-label="Close"
      >
        <X size={16} />
      </button>
      <img
        src={media.url}
        alt=""
        className="max-w-[90vw] max-h-[85vh] object-contain rounded-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="update-lightbox-image"
      />
    </div>,
    document.body,
  );
}

/** Standalone reply row — Babel-loader recursed infinitely when UpdateRow
 *  referenced itself in JSX (@babel/traverse call-stack blew).
 *  ReplyRow re-uses the same visual template but breaks the static cycle
 *  by NOT referencing UpdateRow's identifier. */
function ReplyRow({ u, user, onRemove, onReact, onOpenLightbox }) {
  const mine = u.user_id === user?.id;
  const can = usePermissions();
  const isAdmin = can("projects_edit");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const reactions = u.reactions || {};
  return (
    <div className="card-flat p-4 ml-8 border-l-2 border-l-slate-200 rounded-l-none" data-testid={`update-${u.id}`}>
      <div className="flex items-start gap-3">
        {u.user_avatar ? (
          <img src={u.user_avatar} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-6 h-6 text-[10px] rounded-full bg-[#F54800]/10 text-[#F54800] flex items-center justify-center font-bold shrink-0">{u.user_name?.[0] || "?"}</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="text-slate-900 font-medium">{u.user_name}</span>
            <span>· {u.user_role}</span>
            <span>· {formatDateTime(u.created_at)}</span>
          </div>
          {u.body && <p className="text-xs text-slate-800 mt-1 whitespace-pre-wrap break-words">{linkify(u.body)}</p>}
          {u.media_url && (
            <button
              type="button"
              onClick={() => u.media_type !== "video" && onOpenLightbox({ url: u.media_url, type: u.media_type })}
              className={`mt-2 rounded-sm overflow-hidden border border-slate-100 bg-slate-50 max-w-md block ${u.media_type !== "video" ? "cursor-zoom-in hover:border-slate-300" : ""}`}
              data-testid={`update-media-${u.id}`}
            >
              {u.media_type === "video"
                ? <video src={u.media_url} controls className="w-full max-h-96" />
                : <img src={u.media_url} alt="Attachment" className="w-full max-h-96 object-contain" />}
            </button>
          )}
          {Object.keys(reactions).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1" data-testid={`reactions-${u.id}`}>
              {Object.entries(reactions).map(([emoji, users]) => {
                const mineHere = users.includes(user?.id);
                return (
                  <button
                    key={emoji}
                    onClick={() => onReact(u.id, emoji)}
                    className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-sm border ${mineHere ? "bg-[#F54800]/10 border-[#F54800]/40 text-[#F54800]" : "bg-slate-50 border-slate-200 hover:border-slate-400"}`}
                    data-testid={`reaction-${u.id}-${emoji}`}
                  >
                    <span>{emoji}</span><span className="font-mono">{users.length}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="mt-2 flex items-center gap-1 flex-wrap">
            <div className="relative">
              <button onClick={() => setEmojiOpen((v) => !v)} className="text-[10px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm hover:bg-slate-100" data-testid={`react-open-${u.id}`}>
                <Smile size={11} /> React
              </button>
              {emojiOpen && (
                <div className="absolute z-30 mt-1 left-0 bg-white border border-slate-200 rounded-sm shadow-lg p-2 flex flex-wrap gap-1 max-w-[220px]" data-testid={`emoji-panel-${u.id}`} onMouseLeave={() => setEmojiOpen(false)}>
                  {[...QUICK_EMOJIS, ...EXTRA_EMOJIS].map((e) => (
                    <button key={e} onClick={() => { onReact(u.id, e); setEmojiOpen(false); }} className="text-lg hover:bg-slate-100 rounded-sm w-7 h-7 leading-7" data-testid={`emoji-btn-${u.id}-${e}`}>{e}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {(mine || isAdmin) && (
          <button onClick={() => onRemove(u.id)} className="text-slate-400 hover:text-red-600 shrink-0" data-testid={`del-update-${u.id}`}><Trash2 size={13} /></button>
        )}
      </div>
    </div>
  );
}
